/**
 * Actions on a selection that start heavy work per asset (1.0.0-rc.13 second round, Mikael): Run QC, Send to edit
 * (a clip hand-off) and Restore act on at most this many assets at a time, for every user including administrators,
 * and a user has at most this many of each in progress at once (manually started; flows, watch folders and the
 * restores of "Restore and export" / "Restore and start" have their own limits). The API answers 422 TOO_MANY_ASSETS
 * and 429 TOO_MANY_RUNNING beyond them; the Library disables the actions above it.
 *
 * 1.2.0: batch Export and batch Run workflow have the same limits. Only exports started by the batch route count
 * (exports.batch_id; single-asset exports and flow exports do not), and manual workflow runs count while queued,
 * waiting for resources, running or retrying (a run waiting for an approval does not).
 */
import { z } from 'zod';
import { EXPORT_FORMAT_IDS } from './graphics.ts';
import { MasterChoiceSchema } from './masters.ts';
import { RightsAckSchema } from './rights.ts';
import { SidecarFormatsSchema } from './subtitles.ts';

export const BULK_ACTION_LIMIT = 20;

export const tooManyAssetsMessage = (limit = BULK_ACTION_LIMIT) => `Select at most ${limit} assets at a time`;

/** What a person may have at most BULK_ACTION_LIMIT of in progress (services/api/src/modules/bulk-limits.ts). */
export const IN_FLIGHT_KINDS = ['qc', 'handoff', 'restore', 'export', 'workflow'] as const;
export type InFlightKind = (typeof IN_FLIGHT_KINDS)[number];

const AssetIds = z.array(z.string().uuid()).min(1).max(BULK_ACTION_LIMIT);

/**
 * POST /api/v1/exports/batch: each asset whole or its programme range, in one format, to one destination. Burn-in and
 * marked ranges stay single-asset (set per asset). subtitleMode sidecar delivers each asset's default subtitle track
 * (skipped quietly for an asset without one). master: which master each asset's export reads (default: Programme,
 * the format closest to the preset; Original format: the primary unless a variant is chosen).
 */
export const BatchExportBody = z.object({
  assetIds: AssetIds,
  preset: z.enum(EXPORT_FORMAT_IDS),
  quality: z.enum(['original', 'proxy']).default('original'),
  range: z.enum(['full', 'programme']).default('full'),
  locationId: z.string().uuid().nullable().default(null),
  subtitleMode: z.enum(['none', 'sidecar']).default('none'),
  /** 1.9.0: the sidecar formats delivered with subtitleMode sidecar or both (SidecarFormatsSchema); left out: SRT and WebVTT. */
  sidecarFormats: SidecarFormatsSchema.optional(),
  master: MasterChoiceSchema.optional(),
  /** Restore archived originals first; those exports wait for them. */
  restore: z.boolean().default(false),
  /** 1.8.0: the rights warnings confirmed, or an override with its reason (rights.manage), for every asset of the batch. */
  rights: RightsAckSchema.optional(),
});
export type BatchExportBody = z.infer<typeof BatchExportBody>;

/** POST /api/v1/automations/:id/runs/batch: one manual run per asset, with the single run's checks. */
export const BatchRunBody = z.object({
  assetIds: AssetIds,
  restore: z.boolean().default(false),
  /** 1.8.0: as on a single manual run (kept on each run's trigger.rights). */
  rights: RightsAckSchema.optional(),
});
export type BatchRunBody = z.infer<typeof BatchRunBody>;

/** Why one asset of a batch did not start (the error code its single-asset start would answer, and the message). */
export interface BatchSkipped {
  assetId: string;
  title: string | null;
  code: string;
  message: string;
}

/**
 * What a batch started. overLimit: assets not started because the person reached BULK_ACTION_LIMIT in progress
 * (they are also listed in skipped, with code TOO_MANY_RUNNING).
 */
export interface BatchResult {
  started: Array<{ assetId: string; id: string }>;
  skipped: BatchSkipped[];
  overLimit: number;
}

/** GET /api/v1/preflight?…&assetIds=a,b,c (1.2.0): the batch dialogs' per-asset answer. */
export interface BatchPreflightResult {
  ok: boolean;
  /** 1.8.0: a problem's severity (rights.ts PreflightSeverity); absent: block, as every problem before 1.8.0. */
  items: Array<{ assetId: string; title: string | null; ok: boolean; problems: Array<{ code: string; message: string; severity?: import('./rights.ts').PreflightSeverity }>; restorable?: true }>;
}
